import { jsx } from 'react/jsx-runtime';
import { EGDSLayoutTags } from '../../layout/types/index.esm.js';
import 'react';
import '../../utils/index_orig/index.esm.js';
import '../../utils/theme/theme-provider/index.esm.js';
import { MinMax, isAutoRepeatGrid, isSmallKey, MaketrackItem } from '../functions/index.esm.js';
import styles from '../../styles/uitk-egds-layout-grid.css';
import { classNames } from '../../utils/classnames/index.esm.js';
const getClassNameList = classNames.bind(styles);
const baseClass = 'uitk-layout-grid';
/**
 * Set a valid grid track value
 *
 * @param value {any}
 * @returns string
 */
const trackList = value => {
  const track = [];
  const isArray = Array.isArray(value);
  const isNumber = typeof value === 'number';
  if (isArray) {
    value.forEach(item => track.push(MaketrackItem(item)));
  } else if (isNumber) {
    track.push(`repeat(${value}, minmax(0, 1fr))`); // Include minmax() to ensure unbroken content doesn't force grid out
  } else {
    track.push(MaketrackItem(value));
  }
  return track.join(' ');
};
/**
 * Generate a valid CSS custom prop / value object for grid areas
 *
 * @param areas {UitkLayoutGridArea | UitkLayoutConditionalGridArea}
 * @returns Object as React.CSSProperties
 */
const areasValue = areas => {
  const isObject = !Array.isArray(areas) && typeof areas === 'object';
  const obj = {};
  const areaRows = value => value.map(area => `"${area}"`).join(' ');
  if (isObject) {
    Object.entries(areas).map(([key, val]) => Object.assign(obj, {
      [`--uitk-layoutgrid-areas${isSmallKey(key) ? '' : `-${key}`}`]: areaRows(val)
    }));
  } else {
    Object.assign(obj, {
      [`--uitk-layoutgrid-areas`]: areaRows(areas)
    });
  }
  return obj;
};
/**
 * Generate a valid CSS custom prop / value object for grid gap
 *
 * @param value {UitkLayoutSpace | UitkLayoutGridSpaceInterface}
 * @returns Object as React.CSSProperties
 */
const spaceValue = value => {
  const isString = typeof value === 'string';
  const colValue = isString ? value : value.column;
  const rowValue = isString ? value : value.row;
  return {
    ...(colValue && {
      [`--uitk-layoutgrid-column-gap`]: `var(--uitk-layoutgrid-space-${colValue})`
    }),
    ...(rowValue && {
      [`--uitk-layoutgrid-row-gap`]: `var(--uitk-layoutgrid-space-${rowValue})`
    })
  };
};
/**
 * Set a valid value for the deprecated UitkLayoutGridAutoRepeat interface
 *
 * @param repeatType {string} Type of repeat
 * @param minValue {any} min value for the minmax() part of the repeat() function
 * @param suffix {string} suffix for the custom property name
 * @returns object as React.CSSProperties
 */
const autoRepeatGridValue = (repeatType, minValue, suffix) => {
  const newRepeatType = repeatType.replace('_', '-');
  return {
    [`--uitk-layoutgrid-${suffix}`]: `repeat(${newRepeatType}, minmax(var(--uitk-layoutgrid-egds-size__${minValue}), 1fr))`
  };
};
/**
 * Generate a valid CSS custom prop / value object for:
 *
 * - columns
 * - rows
 * - autoColumns
 * - autoRows
 *
 * @param value {UitkLayoutSpace | UitkLayoutGridSpaceInterface}
 * @returns Object as React.CSSProperties
 */
const gridTrackValue = (value, suffix) => {
  const isObject = !Array.isArray(value) && typeof value === 'object';
  const obj = {};
  if (isObject) {
    Object.entries(value).map(([key, val]) => Object.assign(obj, isAutoRepeatGrid(value) ? autoRepeatGridValue(key, val, suffix) : {
      [`--uitk-layoutgrid-${suffix}${isSmallKey(key) ? '' : `-${key}`}`]: trackList(val)
    }));
  } else {
    Object.assign(obj, {
      [`--uitk-layoutgrid-${suffix}`]: trackList(value)
    });
  }
  return obj;
};
/**
 * Generate a valid CSS custom prop for blockSize
 *
 * @param value {UitkLayoutGridBlockSize}
 * @param suffix {string}
 * @returns Object as React.CSSProperties
 */
const blockSizeValue = (value, suffix) => {
  const obj = {};
  let newValue;
  if (value !== 'full_size') {
    // If value is a viewport block (vb) length,
    // convert to viewport height (vh) for the time being,
    // until browser support matches our requirements
    // https://caniuse.com/mdn-css_types_length_vb
    newValue = value.replace('vb', 'vh');
    Object.assign(obj, {
      [`--uitk-layoutgrid-${suffix}`]: newValue
    });
  }
  return obj;
};
/**
 * Prevent invalid CSS Custom Properties from being applied
 *
 * @param value {UitkLayoutGridArea | UitkLayoutConditionalGridArea | UitkLayoutGridTrack | UitkLayoutConditionalGridTrack}
 * @param suffix {string}
 * @param baseCSSClass {string}
 * @returns getClassNameList()
 */
const conditionalGridTrackClassName = (value, suffix, baseCSSClass = baseClass) => {
  const isObject = !Array.isArray(value) && typeof value === 'object';
  const isautoGrid = isObject && (Object.prototype.hasOwnProperty.call(value, 'auto_fill') || Object.prototype.hasOwnProperty.call(value, 'auto_fit'));
  return getClassNameList(isObject ? Object.entries(value).map(([key]) => `${baseCSSClass}-has-${suffix}${isSmallKey(key) ? '' : `-by-${key}`}`) : `${baseCSSClass}-has-${suffix}`, isautoGrid && `${baseCSSClass}-has-${suffix}-using-auto-grid`);
};
const conditionalBlockSizeClassName = (value, suffix, baseCSSClass = baseClass) => value === 'full_size' ? `${baseCSSClass}-${suffix}-${value}` : `${baseCSSClass}-${suffix}-explicit_size`;
const wrapperClassNames = ({
  areas,
  alignContent,
  alignItems,
  autoFlow,
  autoColumns,
  autoRows,
  blockSize,
  className,
  columns,
  display,
  justifyContent,
  justifyItems,
  minBlockSize,
  rows,
  space
}) => getClassNameList(baseClass, alignContent && `${baseClass}-align-content-${alignContent}`, alignItems && `${baseClass}-align-items-${alignItems}`, areas && conditionalGridTrackClassName(areas, 'areas'), autoColumns && `${baseClass}-has-auto-columns`, autoRows && `${baseClass}-has-auto-rows`, columns && conditionalGridTrackClassName(columns, 'columns'), rows && conditionalGridTrackClassName(rows, 'rows'), space && `${baseClass}-has-space`, autoFlow && `${baseClass}-auto-flow-${autoFlow}`, blockSize && conditionalBlockSizeClassName(blockSize, 'block-size'), display && `${baseClass}-display-${display}`, justifyContent && `${baseClass}-justify-content-${justifyContent}`, justifyItems && `${baseClass}-justify-items-${justifyItems}`, minBlockSize && `${baseClass}-min-block-size-${minBlockSize}`, className);
const EGDSLayoutGrid = props => {
  const {
    alignContent,
    alignItems,
    areas,
    autoColumns = [MinMax('0x', '1fr')],
    autoFlow,
    autoRows,
    blockSize,
    children,
    display = 'grid',
    space,
    columns,
    className,
    justifyContent,
    justifyItems,
    minBlockSize,
    rows,
    tag,
    dataTestId,
    style,
    ...attributes
  } = props;
  const wrapperClassNameProps = {
    alignContent,
    alignItems,
    autoFlow,
    autoColumns,
    autoRows,
    areas,
    blockSize,
    className,
    columns,
    display,
    justifyContent,
    justifyItems,
    minBlockSize,
    rows,
    space
  };
  const gridCustomProps = {
    ...(areas && areasValue(areas)),
    ...(autoColumns && gridTrackValue(autoColumns, 'auto-columns')),
    ...(autoRows && gridTrackValue(autoRows, 'auto-rows')),
    ...(columns && gridTrackValue(columns, 'columns')),
    ...(rows && gridTrackValue(rows, 'rows')),
    ...(space && spaceValue(space)),
    ...(blockSize && blockSizeValue(blockSize, 'block-size')),
    ...(style && style)
  };
  // Renders the custom Tag
  const Tag = tag && EGDSLayoutTags.get(tag) || EGDSLayoutTags.get('div');
  return children ? jsx(Tag, {
    className: wrapperClassNames({
      ...wrapperClassNameProps
    }),
    style: gridCustomProps,
    ...attributes,
    ...(dataTestId && {
      'data-test-id': dataTestId
    }),
    children: children
  }) : null;
};
EGDSLayoutGrid.displayName = 'EGDSLayoutGrid';
const withEGDSLayoutGrid = WrappedComponent => {
  const WithEGDSLayoutGrid = props => {
    const {
      alignContent,
      alignItems,
      autoColumns,
      autoFlow,
      autoRows,
      areas,
      blockSize,
      className,
      children,
      columns,
      display,
      justifyContent,
      justifyItems,
      minBlockSize,
      rows,
      space,
      style
    } = props;
    const wrapperClassNameProps = {
      alignContent,
      alignItems,
      autoFlow,
      autoColumns,
      autoRows,
      areas,
      blockSize,
      className,
      columns,
      display,
      justifyContent,
      justifyItems,
      minBlockSize,
      rows,
      space
    };
    const gridCustomProps = {
      ...(areas && areasValue(areas)),
      ...(autoColumns && gridTrackValue(autoColumns, 'auto-columns')),
      ...(autoRows && gridTrackValue(autoRows, 'auto-rows')),
      ...(columns && gridTrackValue(columns, 'columns')),
      ...(rows && gridTrackValue(rows, 'rows')),
      ...(space && spaceValue(space)),
      ...(blockSize && blockSizeValue(blockSize, 'block-size')),
      ...(style && style)
    };
    return jsx(WrappedComponent, {
      className: wrapperClassNames({
        ...wrapperClassNameProps
      }),
      style: gridCustomProps,
      children: children || null
    });
  };
  WithEGDSLayoutGrid.displayName = `withUitkLayoutGrid(${WrappedComponent.displayName})`;
  return WithEGDSLayoutGrid;
};
export { EGDSLayoutGrid, withEGDSLayoutGrid };